import React, { useContext, useEffect, useState } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { ref, set } from "firebase/database";
import { auth, db } from "../api/firebase";
import type { UserRecord } from "../types";

interface AuthContextType {
  currentUser: User | null;
  authUID: string | undefined;
  fakeUID: string | undefined;
  effectiveUID: string | undefined;
  setFakeUID: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [pending, setPending] = useState(true);
  const [authUID, setAuthUIDState] = useState<string | undefined>(undefined);
  const [fakeUID, setFakeUID] = useState<string | undefined>(undefined);

  const effectiveUID = fakeUID || authUID;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthUIDState(user.uid);
        // Write user record directly to Firebase
        set(ref(db, `/users/${user.uid}`), {
          uid: user.uid,
          name: user.displayName,
          email: user.email,
          photoURL: user.photoURL,
        } satisfies UserRecord);
      } else {
        setAuthUIDState(undefined);
      }
      setCurrentUser(user);
      setPending(false);
    });
    return () => unsubscribe();
  }, []);

  if (pending) {
    return <>Loading...</>;
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        authUID,
        fakeUID,
        effectiveUID,
        setFakeUID,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const superUserUID = "68jLhdzhOAPhZVGnTsJBynJQEuG3";

export const getDisplayName = (user: UserRecord): string => {
  return user.name ? user.name : user.email ? user.email : user.uid;
};
